/**
 * The game's audio (GDD §9): starts on the first tap, click or key press (judgment call 45), hears the simulation's
 * effects through `game.hooks.sound`, and reads the run every frame: the listener at the jet, the cannon's volleys,
 * the engine, the nearest helicopters, gunboats and enemy jets, the refuel tone and alarm, and the music's
 * intensity. Pause and hidden tabs suspend it (judgment call 47). Nothing here allocates per frame (GDD §10.5).
 */
import { config } from '../core/config.ts';
import type { Game } from '../game/game.ts';
import type { FlowState } from '../hud/hud.ts';
import { refueling } from '../rules/fuel.ts';
import type { GameSim } from '../sim/context.ts';
import type { SoundTap } from '../sim/fx.ts';
import type { Enemy, SimEvent } from '../sim/state.ts';
import type { Settings } from '../storage/storage.ts';
import type { LoopKind } from './loops.ts';
import type { SfxKind } from './sfx.ts';
import { createSoundEngine, LOOP_COUNT, type SoundEngine } from './soundEngine.ts';
import {
  doppler,
  followIntensity,
  INCOMING_FULL,
  LOOP_RANGE,
  musicTarget,
  NEAR_AHEAD,
  NEAR_BEHIND,
  NEAR_FULL,
  nearest,
} from './tuning.ts';

export interface AudioSnapshot {
  /** 'locked' until the first gesture; then the context's state. */
  state: 'locked' | 'unavailable' | AudioContextState;
  masterTarget: number;
  musicTarget: number;
  effectsTarget: number;
  demo: boolean;
  played: Record<SfxKind, number>;
  busy: number;
  /** Voices following an enemy, per kind. */
  loops: Record<LoopKind, number>;
  /** Doppler factors of the enemy-jet voices that follow a jet. */
  jetDoppler: number[];
  engine: { speed: number; alive: boolean; burner: number; rumbleHz: number };
  refuelBlips: number;
  alarmFigures: number;
  intensity: number;
  notes: number;
}

export interface GameAudio {
  /** Starts audio (a gesture handler calls this; tests may too). */
  unlock(): void;
  settings(s: Settings): void;
  pause(): void;
  resume(): void;
  event(e: SimEvent): void;
  /** Once a frame, after the HUD. */
  frame(state: FlowState): void;
  snapshot(): AudioSnapshot;
  /** The sound engine once audio has started (the sound check and tests). */
  readonly engine: SoundEngine | null;
}

export interface GameAudioOptions {
  game: Game;
  settings: Settings;
  win?: Window;
  doc?: Document;
}

const KINDS: readonly LoopKind[] = ['heli', 'ship', 'jet'];
const noop = (): void => undefined;

export function createGameAudio(o: GameAudioOptions): GameAudio {
  const win = o.win ?? window;
  const doc = o.doc ?? document;
  const game = o.game;
  if (!game.sim) throw new Error('audio: the game has no simulation (classic mode only)');
  const sim: GameSim = game.sim;
  let settings: Settings = { ...o.settings };
  let ctx: AudioContext | null = null;
  let se: SoundEngine | null = null;
  const st = { failed: false, paused: false, hidden: false, burst: 0, frame: 0, last: 0, intensity: 0, near: 0 };

  // Per-frame scratch for choosing the nearest enemies (pools never exceed this).
  const cap = Math.max(64, sim.ents.items.length);
  const dist = new Float64Array(cap);
  const pick = new Int32Array(3);
  const voiceOf = new Int32Array(3);
  const claimed = new Uint8Array(3);

  function running(): boolean {
    return !st.paused && !st.hidden;
  }

  function apply(): void {
    if (!se) return;
    se.begin();
    se.volumes(settings.music, settings.effects, settings.mute);
  }

  function unlock(): void {
    if (st.failed) return;
    if (!ctx) {
      try {
        const Ctor = (win as Window & { AudioContext?: typeof AudioContext }).AudioContext;
        if (!Ctor) throw new Error('no Web Audio');
        // iPhone: respect the silent switch and mix with the player's own music (judgment call 46).
        const session = (win.navigator as Navigator & { audioSession?: { type: string } }).audioSession;
        if (session) session.type = 'ambient';
        const made = new Ctor({ latencyHint: 'interactive' });
        ctx = made;
        se = createSoundEngine(made);
        st.burst = sim.guns.burst;
        apply();
      } catch {
        st.failed = true;
        ctx = null;
        se = null;
        return;
      }
    }
    // Also brings audio back after the phone interrupted it (a call, Siri).
    const c = ctx as AudioContext | null;
    if (c && running() && c.state !== 'running') c.resume().catch(noop);
  }

  for (const type of ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'])
    win.addEventListener(type, unlock, { capture: true, passive: true });
  doc.addEventListener('visibilitychange', () => {
    st.hidden = doc.visibilityState === 'hidden';
    if (!ctx) return;
    if (st.hidden) ctx.suspend().catch(noop);
    else if (running()) ctx.resume().catch(noop);
  });

  /** The nearest live enemies of each kind get the continuous voices; voices stick to the enemy they follow. */
  function enemies(s: SoundEngine): void {
    const jet = sim.jet;
    const ents = sim.ents;
    const n = Math.min(ents.count, cap);
    let near = 0;
    for (let i = 0; i < n; i++) {
      const e = ents.items[i];
      if (!e || e.dying > 0 || e.type === 'fuel') continue;
      const rz = e.posZ - jet.z;
      if (rz > -NEAR_AHEAD && rz < NEAR_BEHIND) near++;
    }
    st.near = near;
    for (let k = 0; k < KINDS.length; k++) {
      const kind = KINDS[k] as LoopKind;
      const voices = LOOP_COUNT[kind];
      for (let i = 0; i < n; i++) {
        const e = ents.items[i];
        if (!e || e.type !== kind || e.dying > 0) {
          dist[i] = Infinity;
          continue;
        }
        const rx = e.posX - jet.x;
        const ry = e.posY - jet.y;
        const rz = e.posZ - jet.z;
        dist[i] = Math.sqrt(rx * rx + ry * ry + rz * rz);
      }
      const m = nearest(dist, n, voices, LOOP_RANGE[kind], pick);
      const loops = s.loops[kind];
      for (let v = 0; v < voices; v++) claimed[v] = 0;
      // Enemies that already have a voice keep it.
      for (let c = 0; c < m; c++) {
        const e = ents.items[pick[c] ?? 0];
        voiceOf[c] = -1;
        for (let v = 0; v < voices; v++)
          if (loops[v]?.owner === e) {
            voiceOf[c] = v;
            claimed[v] = 1;
            break;
          }
      }
      // The rest take free voices.
      for (let c = 0; c < m; c++) {
        if ((voiceOf[c] ?? 0) >= 0) continue;
        for (let v = 0; v < voices; v++)
          if (!claimed[v]) {
            voiceOf[c] = v;
            claimed[v] = 1;
            break;
          }
      }
      for (let c = 0; c < m; c++) {
        const e = ents.items[pick[c] ?? 0] as Enemy;
        const rx = e.posX - jet.x;
        const ry = e.posY - jet.y;
        const rz = e.posZ - jet.z;
        // The jet flies upriver (−z); enemy jets cross the river (x).
        const d = kind === 'jet' ? doppler(rx, ry, rz, e.vx, 0, 0, jet.vx, 0, -jet.speed) : 1;
        s.loop(kind, voiceOf[c] ?? 0, e, rx, ry, rz, d);
      }
      for (let v = 0; v < voices; v++) if (!claimed[v]) s.loop(kind, v, null, 0, 0, 0, 1);
    }
  }

  const tap: SoundTap = {
    boom(x, y, z, s) {
      if (se && running()) se.play('boom', x, y, z, s);
    },
    splash(x, z, s) {
      if (se && running()) se.play('splash', x, 0, z, s);
    },
    spanSplash(x, z) {
      if (se && running()) se.play('spanSplash', x, 0, z, 1);
    },
    flak(x, y, z) {
      if (se && running()) se.play('flak', x, y, z, 1);
    },
    muzzle(x, y, z, _vx, _vz, cause) {
      if (se && running())
        se.play(cause === 'flak' ? 'flakLaunch' : cause === 'bridgeGuns' ? 'gunBurst' : 'crack', x, y, z, 1);
    },
    bridgeHit(x, y, z) {
      if (se && running()) se.play('clank', x, y, z, 1);
    },
  };
  game.hooks.sound = tap;
  game.hooks.shift = (dz) => se?.shift(dz);

  const api: GameAudio = {
    unlock,
    get engine() {
      return se;
    },
    settings(s) {
      settings = { ...s };
      apply();
    },
    pause() {
      st.paused = true;
      ctx?.suspend().catch(noop);
    },
    resume() {
      st.paused = false;
      if (ctx && running()) ctx.resume().catch(noop);
    },
    event(e) {
      if (!se || !running()) return;
      if (e.type === 'jetLost') se.duck();
      else if (e.type === 'extraJet' && sim.run.player) se.local('chime', 0);
    },
    frame(state) {
      const s = se;
      if (!s || !running()) return;
      const now = s.begin();
      const dt = Math.min(Math.max(now - st.last, 0), 0.25);
      st.last = now;
      const jet = sim.jet;
      const run = sim.run;
      const play = state === 'play';
      s.demo(state === 'title' || state === 'loading');
      s.listener(jet.x, jet.y, jet.z);
      // The cannon: one report per volley fired since the last frame (judgment call 25 spaces them 0.09 s apart).
      const volleys = sim.guns.burst - st.burst;
      st.burst = sim.guns.burst;
      if (volleys > 0) for (let i = 0; i < Math.min(volleys, 3); i++) s.local('cannon', i * config.guns.interval);
      s.engine(jet.speed, run.alive);
      // Enemy voices and one-shots move smoothly between updates, so each updates every other frame.
      const even = (st.frame++ & 1) === 0;
      if (even) enemies(s);
      // Signals belong to the player, not the title demo (judgment call 48).
      const player = play && run.player && run.alive;
      const refuel = player && refueling(sim);
      const alarm = player && !refuel && run.fuel < config.fuel.lowThreshold;
      if (refuel || alarm || s.signals.nextRefuel || s.signals.nextAlarm) s.setSignals(refuel, alarm, run.fuel);
      const target = musicTarget(
        play,
        run.level,
        st.near / NEAR_FULL,
        sim.shots.count / INCOMING_FULL,
        play && run.fuel < config.fuel.lowThreshold,
        sim.bridge.engaged,
      );
      st.intensity = followIntensity(st.intensity, target, dt);
      s.setMusic(st.intensity);
      s.update(!even);
    },
    snapshot() {
      const s = se;
      const loops = { heli: 0, ship: 0, jet: 0 };
      const jetDoppler: number[] = [];
      if (s)
        for (const kind of KINDS)
          for (const v of s.loops[kind])
            if (v.owner) {
              loops[kind]++;
              if (kind === 'jet') jetDoppler.push(v.doppler);
            }
      return {
        state: st.failed ? 'unavailable' : ctx ? ctx.state : 'locked',
        masterTarget: s?.mixer.masterTarget ?? 0,
        musicTarget: s?.mixer.musicTarget ?? 0,
        effectsTarget: s?.mixer.effectsTarget ?? 0,
        demo: s?.mixer.demo ?? false,
        played: { ...(s?.stats.played ?? ({} as Record<SfxKind, number>)) },
        busy: s?.stats.busy ?? 0,
        loops,
        jetDoppler,
        engine: {
          speed: s?.engineVoice.speed ?? 0,
          alive: s?.engineVoice.alive ?? false,
          burner: s?.engineVoice.tone.burner ?? 0,
          rumbleHz: s?.engineVoice.tone.rumbleHz ?? 0,
        },
        refuelBlips: s?.signals.blips ?? 0,
        alarmFigures: s?.signals.figures ?? 0,
        intensity: st.intensity,
        notes: s?.music.notes ?? 0,
      };
    },
  };
  return api;
}
